// ============================================================================
// BudgetComparisonCard (E2, F-523) — reel vs budget par categorie
// ============================================================================
// Portage du PoC components/finance/budget-comparison-card.tsx : barre de progression
// coloree (reel) sur piste neutre, repere vertical = budget. La normalisation est
// COMMUNE a toute la carte (max de la carte) pour que les barres restent comparables
// entre categories — un max par ligne rendrait la comparaison mensongere.
// Couleurs : categoryColor (lib portee) -> var(--chart-cat-N) (index.css, BL-12).

import type { TFunction } from 'i18next';
import type { StatementLine } from '../../lib/finance/computations';
import type { FinanceSection } from '../../lib/finance/catalog';
import { categoryColor } from '../../lib/finance/palette';
import { formatCad, formatVariancePct, varianceToneClass } from '../../pages/finance/format';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

export interface BudgetComparisonCardProps {
  title: string;
  section: FinanceSection;
  lines: StatementLine[];
  locale: string;
  t: TFunction;
}

export function BudgetComparisonCard({ title, section, lines, locale, t }: BudgetComparisonCardProps) {
  const max = Math.max(1, ...lines.map((l) => Math.max(l.actual, l.budget, l.priorYear)));
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;

  return (
    <section className={CARD}>
      <h3 className="text-head font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
        {t('gest:finance.budgetCompare.legend')}
      </p>

      <ul className="mt-3 flex flex-col gap-3">
        {lines.map((l) => (
          <li key={l.code} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-field">
              <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">{l.label}</span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className={`text-meta tabular-nums ${varianceToneClass(l.varianceBudget)}`}>
                  {formatVariancePct(l.pctVsBudget, locale)}
                </span>
                <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
                  {formatCad(l.actual, locale)}
                </span>
                <span className="text-meta tabular-nums text-slate-400 dark:text-slate-500">
                  / {formatCad(l.budget, locale)}
                </span>
              </span>
            </div>
            <div
              className="relative h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
              role="img"
              aria-label={t('gest:finance.budgetCompare.aria', {
                label: l.label,
                actual: formatCad(l.actual, locale),
                budget: formatCad(l.budget, locale),
                priorYear: formatCad(l.priorYear, locale),
              })}
            >
              <div
                className="h-full rounded-full"
                style={{ width: pct(l.actual), backgroundColor: categoryColor(section, l.code) }}
              />
              {l.budget > 0 ? (
                <div
                  aria-hidden="true"
                  className="absolute inset-y-0 w-0.5 bg-slate-800 dark:bg-slate-200"
                  style={{ left: pct(l.budget) }}
                />
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
